import { forwardRef, useEffect, useRef } from "react";

import { CommandItem, CommandList } from "cmdk";

import { closeCommandPalette } from "../../commands";
import { ContentType } from "../../lib/__generated__/graphql";
import { sendCustomGAEvent } from "../../utils/google-event";
import { SearchItem } from "../search-item/search-item";
import SyncLoader, {
  CommandPaletteAltStates,
} from "./command-palette-alt-states";

type Props = {
  options: {
    id: string;
    title: string;
    link: string;
    description?: string;
    type: ContentType;
  }[];
  query: string;
  isLoading: boolean;
  isFetching: boolean;
  totalOptions: number;
  clearQuery: () => void;
};

export const CommandPaletteBody = forwardRef<HTMLDivElement, Props>(
  (
    { options, isFetching, query, isLoading, totalOptions, clearQuery },
    scrollRef,
  ) => {
    const commandPaletteRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      /* Effect to scroll to top on search value change
      --------------------------------------------
      - query: is a debounced value of the search input.
      - This is particularly useful to prevent cached API calls from keeping the scroll position.
      */
      if (commandPaletteRef.current) {
        commandPaletteRef.current.scrollTop = 0;
      }
    }, [query]);

    return (
      <CommandList
        ref={commandPaletteRef}
        className="plh-h-[379px] plh-w-full plh-overflow-y-auto md-h:plh-h-[300px]"
      >
        <CommandPaletteAltStates
          isLoading={isLoading}
          hasOptions={totalOptions > 0}
          query={query}
        />

        {options.map((option) => (
          <CommandItem
            key={option.id}
            ref={
              options.indexOf(option) === options.length - 1 ? scrollRef : null
            }
            className="data-[selected=true]:plh-bg-brand-blue-8"
            onSelect={(value) => {
              closeCommandPalette();
              clearQuery();
              sendCustomGAEvent('click', {
                event_category: 'com_pal',
                event_label: "search_item_com_pal_click",
                value: option.title,
              });
              window.location.href = value;
            }}
            value={option.link}
          >
            <SearchItem
              title={option.title}
              description={option?.description || ""}
              type={option.type}
            />
          </CommandItem>
        ))}
        {!isLoading && isFetching && (
          <div className="plh-flex plh-h-[80px] plh-items-center plh-justify-center">
            <SyncLoader />
          </div>
        )}
      </CommandList>
    );
  },
);
